import { initRouter, rerenderCurrentRoute } from './utils/router.js';
import { initRoutes } from './routes/index.js';
import { subscribeToAllStores, getAllStoreStates, hydrateStores, excludeFromSync } from './store/index.js';
import { initStores } from './store/index.js';
import { initEffects } from './effects/index.js';
import { initActions, addGlobalListener } from './actions/index.js';
import { select } from './store/index.js';
import { initTheme } from './utils/theme.js'; // Theme initialization
import { initFirebase, setupAuthStateListener } from './config/firebase.js'; // Firebase initialization

/**
 * Detect the application mode from the URL pathname
 * @returns {'demo' | 'app'} The application mode
 */
function getMode() {
  const pathname = window.location.pathname;
  if (pathname.startsWith('/demo')) {
    return 'demo';
  }
  return 'app';
}

// Initialize theme before anything else renders
initTheme();

// Initialize systems based on mode
const mode = getMode();

async function initializeApp() {
  // Initialize core systems (always needed)
  await initActions();
  await initStores();
  
  // Mode-specific initialization
  if (mode === 'demo') {
    // Demo mode: Load mock data, NO effects (prevents I/O)
    // Exclude user store from cross-tab sync so separate windows can
    // be logged in as different demo users simultaneously.
    excludeFromSync('user');
    const mockDataModule = await import('./e2e/fixtures/store.js');
    hydrateStores(mockDataModule.default, false);
    // Expose testing API — record every action at every phase, same pattern as Redux DevTools.
    // Tests read window.__sb.actionLog to assert on args/data at any lifecycle phase.
    const actionLog = [];
    addGlobalListener((actionContext, phase) => {
      const stores = getAllStoreStates();
      actionLog.push({
        name: actionContext.name,
        phase,
        args: JSON.parse(JSON.stringify(actionContext.args ?? [])),
        data: actionContext.data !== undefined ? JSON.parse(JSON.stringify(actionContext.data)) : undefined,
        stores: JSON.parse(JSON.stringify(stores)),
        timestamp: Date.now(),
      });
    });
    window.__sb = { select, addGlobalListener, actionLog };
  } else {
    // App mode: Initialize Firebase and effects (enables I/O)
    try {
      await initFirebase();
      
      // Initialize effects FIRST so they're registered before auth restoration
      await initEffects();
      
      // Set up Firebase auth state listener to restore persisted sessions
      // This must happen AFTER effects are initialized so the login effect is registered
      // WAIT for initial auth check before proceeding
      await setupAuthStateListener();
    } catch (error) {
      console.error('Failed to initialize Firebase:', error);
      // Could show error UI here if needed
    }
  }
  
  // Initialize routes AFTER auth state is known
  await initRoutes();
  
  // Initialize Redux DevTools (if available)
  if (window.__REDUX_DEVTOOLS_EXTENSION__) {
    const devTools = window.__REDUX_DEVTOOLS_EXTENSION__.connect({
      name: 'Skybolt',
      trace: true,
      traceLimit: 25
    });
    
    devTools.init({});
    
    // Wire actions to Redux DevTools - log each phase with current store state
    addGlobalListener((actionContext, phase) => {
      const stores = getAllStoreStates();
      const actionName = `${actionContext.action.name}_${phase.toUpperCase()}`;
      
      devTools.send({
        type: actionName,
        payload: actionContext,
        meta: {
          phase,
          timestamp: Date.now()
        }
      }, stores);
    });
  }
  
  // Initialize the router when the DOM is ready
  initRouter({
    body: document.getElementById('app'),
    title: document.querySelector('title')
  });
  
  // Wire store to router - re-render on any store change
  // Routes use select() to read current store state
  subscribeToAllStores(() => {
    rerenderCurrentRoute();
  });
}

// Start initialization when DOM is ready
window.addEventListener('DOMContentLoaded', initializeApp);
